import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { FileText, Upload, X, Info } from "lucide-react";
import { useRef, useState } from "react";
import { ACCEPTED_FILE_TYPES } from "@/utils/constant";
import { z } from "zod";
interface FileUploadInputProps {
  name: string;
  label: string;
  value: File | null;
  onChange: (file: File | null) => void;
  onUpload?: (file: File) => Promise<any>;
  onUploadSuccess?: (data: any) => void;
  error?: string;
  toast?: any;
  progress?: number;
  buttonText?: string;
  tooltipText?: string;
  disabled?: boolean;
  isHideLabelName?: boolean;
  isHideUploadIcon?:boolean;
}

export const FileUploadInput: React.FC<FileUploadInputProps> = ({
  label = "Upload File",
  name,
  toast,
  error,
  value,
  onChange,
  onUpload,
  disabled,
  progress,
  buttonText,
  tooltipText,
  onUploadSuccess,
  isHideLabelName,
  isHideUploadIcon
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState(value?.name || "");

  const resumeFileSchema = z
    .any()
    .refine((file) => !file || file instanceof File, {
      message: "Invalid file type.",
    })
    .refine(
      (file) =>
        !file ||
        [
          "application/pdf",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ].includes(file.type),
      {
        message: "Only .pdf or .docx files are allowed.",
      }
    )
    .refine((file) => !file || file.size <= 5 * 1024 * 1024, {
      message: "File must be under 5MB.",
    });

  const handleUpload = async (file: File) => {
    setUploading(true);

    const result = resumeFileSchema.safeParse(file);
    if (!result.success) {
      onChange(null);
      setFileName("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      toast({
        title: "Invalid file",
        description: result.error.errors[0].message,
        variant: "destructive",
      });
      setUploading(false);
      return;
    }

    try {
      if (!onUpload) throw new Error("No upload handler provided");

      const response = await onUpload(file);
      setFileName(file.name);
      onChange(file);
      onUploadSuccess?.(response);
    } catch (err: any) {
      onChange(null);
      setFileName("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      toast({
        title: "Upload failed",
        description:
          err.message || "An error occurred while uploading the file.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setFileName("");
    onChange(null as any);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      {!isHideLabelName && <div className="flex items-center">
        <Label htmlFor={name} className="font-medium text-sm text-gray-700">
          {label}
        </Label>
        <div className="relative group ml-2">
          <Info className="h-4 w-4 text-gray-400" />
          <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
            {tooltipText || "Upload your resume (PDF/DOC, max 5MB)."}
          </div>
        </div>
      </div>}

      {((!isHideUploadIcon && isHideLabelName) || !isHideLabelName) && <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 text-xs"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading || disabled}
      >
        <Upload className="h-3.5 w-3.5 mr-1" />
        {uploading ? "Uploading..." : buttonText}
      </Button>}

      <Input
        ref={fileInputRef}
        id={name}
        type="file"
        accept={ACCEPTED_FILE_TYPES.join(",")}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleUpload(file);
        }}
        className="hidden"
        disabled={disabled}
      />

      {uploading && <Progress value={progress} className="h-2 w-full mt-2" />}

      {(fileName || value?.name) && (
        <div className="flex items-center justify-between border rounded-md px-3 py-2 mt-2 bg-gray-50 text-sm">
          <div className="flex items-center gap-2 text-gray-800">
            <FileText className="w-4 h-4 text-purple-500" />
            <span className="truncate max-w-xs">{fileName || value?.name}</span>
          </div>
          <button
            type="button"
            onClick={removeFile}
            className="text-red-500 hover:text-red-700"
            title="Remove file"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};
